// CRAM Configuration portal — Task 5 of docs/CRAM_PLAN.md.
//
// Top-level page with two tabs:
//   - "Lists":    8 seeded lists under `cram_lists/{listId}/items`,
//                 rendered through CramListEditor's per-list mode
//                 (view / score_only / full — see cramListsRegistry.ts).
//   - "Settings": weights, buckets, expiry, and audience knobs at
//                 `config/cram`.
//
// Access control: the sidebar entry is filtered by `routesPermissions` and
// the page itself is gated by the `admin` or `cram` custom claim. Who can
// see / manage CRAM Configuration is assigned in Settings → Claims (the
// `cram` group), which maps a Microsoft 365 group to the `cram` claim. List
// edits here write directly to Firestore; Task 6's preview-before-save flow
// will wrap them in a callable later (see plan §5.3 / §6).
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Container,
    Grid,
    Group,
    Loader,
    Space,
    Tabs,
    Text,
    Title,
} from "@mantine/core";
import {
    IconAdjustments,
    IconAlertCircle,
    IconList,
    IconShieldLock,
} from "@tabler/icons-react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { useFirestore } from "../../helpers/firebaseContext";
import { MyUserDataContext } from "../../contexts";
import { CramConfig, CustomClaims } from "../../types";
import { CRAM_LISTS } from "./cramListsRegistry";
import CramListPicker from "./components/CramListPicker";
import CramListEditor from "./components/CramListEditor";
import CramSettingsTab from "./components/CramSettingsTab";

const CramConfiguration: React.FC = () => {
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const claims = (userContext.firebaseTokenResult?.claims
        ?.customClaims as CustomClaims) || {};

    // CRAM users may still edit the lists; only the Configuration settings tab
    // (rollout mode, weights, buckets, expiry, audience) is admin-only.
    const canEditSettings = !!claims.admin;

    const [activeTab, setActiveTab] = useState<string>("lists");
    const [selectedListId, setSelectedListId] = useState<string>(
        CRAM_LISTS[0].id
    );
    const [config, setConfig] = useState<CramConfig | undefined>();
    const [snapshotVersion, setSnapshotVersion] = useState<string | undefined>();
    const [configLoading, setConfigLoading] = useState(true);
    const [itemCounts, setItemCounts] = useState<Record<string, number>>({});

    useEffect(() => {
        // Only subscribe for authorized users — otherwise Firestore rules
        // reject every read and spam the console (the gate below renders the
        // no-access alert for these users anyway).
        if (!claims.admin && !claims.cram) {
            setConfigLoading(false);
            return;
        }
        const unsub = onSnapshot(
            doc(db, "config", "cram"),
            (snap) => {
                if (!snap.exists()) {
                    setConfig(undefined);
                    setConfigLoading(false);
                    return;
                }
                const data = snap.data() as any;
                setConfig({
                    mode: data.mode,
                    weights: data.weights,
                    buckets: data.buckets,
                    expiry: data.expiry,
                    audience: data.audience,
                    activationDate: data.activationDate ?? null,
                });
                setSnapshotVersion(data.snapshotVersion);
                setConfigLoading(false);
            },
            (err) => {
                console.error("[CRAM] config snapshot error:", err);
                setConfigLoading(false);
            }
        );
        return () => unsub();
    }, [db, claims.admin, claims.cram]);

    // Subscribe to item counts for every list so the picker can show
    // counts before AML opens an individual editor. We use lightweight
    // collection snapshots; the editor still publishes its own count for
    // its active list when the user has unsaved adds/deletes — see
    // `handleItemCount` below.
    useEffect(() => {
        // Same access guard as the config listener above — avoids 8 rejected
        // count subscriptions (and the console spam) for unauthorized users.
        if (!claims.admin && !claims.cram) return;
        const unsubs = CRAM_LISTS.map((list) => {
            const ref = collection(db, "cram_lists", list.id, "items");
            return onSnapshot(
                ref,
                (snap) => {
                    setItemCounts((prev) =>
                        prev[list.id] === snap.size
                            ? prev
                            : { ...prev, [list.id]: snap.size }
                    );
                },
                (err) => {
                    console.warn(
                        `[CRAM] count snapshot error for ${list.id}:`,
                        err
                    );
                }
            );
        });
        return () => {
            for (const u of unsubs) u();
        };
    }, [db, claims.admin, claims.cram]);

    // The active editor can override the server count when there are
    // pending buffered adds/deletes so the picker reflects the diff
    // immediately. When the editor unmounts (user switches lists) the
    // global subscription above resumes ownership.
    const handleItemCount = useMemo(
        () => (listId: string, count: number) =>
            setItemCounts((prev) =>
                prev[listId] === count ? prev : { ...prev, [listId]: count }
            ),
        []
    );

    const selectedListMeta = useMemo(
        () =>
            CRAM_LISTS.find((l) => l.id === selectedListId) ?? CRAM_LISTS[0],
        [selectedListId]
    );

    // Defence-in-depth gate (the sidebar is the primary gate; this catches
    // direct-link access). Allowed for admins and members of the `cram`
    // claim group (assigned in Settings → Claims).
    if (!claims.admin && !claims.cram) {
        return (
            <Container py="lg">
                <Alert
                    icon={<IconShieldLock size={16} />}
                    color="gray"
                    variant="light"
                    title="CRAM Configuration"
                >
                    You don't have access to this page. CRAM Configuration is
                    restricted to admin users and members of the CRAM group
                    (assigned in Settings → Claims).
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid py="md">
            <Group justify="space-between" mb="xs">
                <Box>
                    <Title order={2}>CRAM Configuration</Title>
                    <Text size="sm" c="dimmed">
                        Manage the Customer Risk Assessment Methodology
                        predefined lists, category weights, and rating buckets.
                    </Text>
                </Box>
                {configLoading && <Loader size="xs" />}
            </Group>

            <Space h="sm" />

            <Tabs value={activeTab} onChange={(v) => v && setActiveTab(v)}>
                <Tabs.List>
                    <Tabs.Tab value="lists" leftSection={<IconList size={14} />}>
                        Lists
                    </Tabs.Tab>
                    <Tabs.Tab
                        value="settings"
                        leftSection={<IconAdjustments size={14} />}
                    >
                        Settings
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="lists" pt="md">
                    <Grid gap="md">
                        <Grid.Col span={{ base: 12, md: 2 }}>
                            <CramListPicker
                                selectedListId={selectedListId}
                                onSelect={setSelectedListId}
                                itemCounts={itemCounts}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 10 }}>
                            {!configLoading && !config && (
                                <Alert
                                    color="red"
                                    icon={<IconAlertCircle size={16} />}
                                    variant="light"
                                    mb="sm"
                                >
                                    config/cram is missing — rating badges will
                                    show as "—" until the document is created.
                                </Alert>
                            )}
                            <CramListEditor
                                key={selectedListMeta.id}
                                listMeta={selectedListMeta}
                                config={config}
                                onItemCountChange={handleItemCount}
                            />
                        </Grid.Col>
                    </Grid>
                </Tabs.Panel>

                <Tabs.Panel value="settings" pt="md">
                    <CramSettingsTab
                        config={config}
                        snapshotVersion={snapshotVersion}
                        loading={configLoading}
                        canEdit={canEditSettings}
                    />
                </Tabs.Panel>
            </Tabs>
        </Container>
    );
};

export default CramConfiguration;
